import { useState, useCallback } from 'react'

import { TextField } from '@mui/material'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import Container from '@mui/material/Container'
import Typography from '@mui/material/Typography'
import { useQuery } from '@tanstack/react-query'
import {
  Chart as ChartJS,
  Title,
  ArcElement,
  Tooltip,
  Legend,
  ChartData,
} from 'chart.js'
import DatePicker from 'components/DatePicker'
import LoadingFullPage from 'components/LoadingFullPage'
import SelectWithCheckbox, {
  SelectWithCheckboxOptions,
} from 'components/SelectWithCheckbox'
import { QUERY_KEYS } from 'constants/keys'
import { ACTIVITY_STATUS_OPTIONS } from 'constants/types'
import { useGlobalState } from 'contexts/global-state'
import { Pie } from 'react-chartjs-2'
import { ApiService } from 'services/api'
import { CHART_COLORS } from 'utils/chartColors'
import formatCurrencyUnit from 'utils/formatCurrencyUnit'

ChartJS.register(ArcElement, Tooltip, Legend, Title)

type GenerateResultsParams = {
  queryActivityStatus?: { value: string }[] | null
  queryStartDate?: string | null
  queryEndDate?: string | null
}

export default function SalesCharts() {
  const [total, setTotal] = useState<number>(0)
  const [totalRevenue, setTotalRevenue] = useState<number>(0)
  const [totalReceit, setTotalReceit] = useState<number>(0)

  const [totalCostChartData, setTotalCostChartData] = useState<
    ChartData<'pie', number[], unknown> | undefined
  >()

  const [revenueByActivityChartData, setRevenueByActivityChartData] = useState<
    ChartData<'pie', number[], unknown> | undefined
  >()

  const [receitChartData, setReceitChartData] = useState<
    ChartData<'pie', number[], unknown> | undefined
  >()

  const [selectedInstitutions, setSelectedInstitutions] =
    useState<SelectWithCheckboxOptions>([])

  const [selectedBudgets, setSelectedBudgets] =
    useState<SelectWithCheckboxOptions>([])

  const [selectedActivityStatus, setSelectedActivityStatus] =
    useState<SelectWithCheckboxOptions>([])

  const [activityCode, setActivityCode] = useState<string>('')

  const [startDate, setStartDate] = useState<string | Date | null | undefined>(
    null,
  )

  const [endDate, setEndDate] = useState<string | Date | null | undefined>(null)
  const [isLoading, setIsLoading] = useState<boolean>(false)

  const { openErrorToast } = useGlobalState()

  const { data: institutions } = useQuery<GelAllNamesAndIdsOptions[]>(
    QUERY_KEYS.INSTITUTIONS.NAMES_AND_IDS,
    () =>
      ApiService.Institutions.getAllNamesAndIds({
        fields: ['id', 'fantasy_name', 'document', 'type'],
        relations: [],
      }),
  )

  const { data: budgets } = useQuery<SelectWithCheckboxOptions>(
    QUERY_KEYS.BUDGETS.LIST,
    () => ApiService.Budgets.getAllNamesAndIds(),
  )

  const generateReport = useCallback(
    ({
      queryStartDate,
      queryEndDate,
      queryActivityStatus,
    }: GenerateResultsParams) => {
      setIsLoading(true)
      ApiService.Reports.generateRevenueCost({
        institutions: selectedInstitutions.map((si) => Number(si.value)),
        status: ['finished', 'payment_confirmed'],
        activityStatus: (queryActivityStatus || selectedActivityStatus).map(
          (s) => String(s.value),
        ),
        budgets: selectedBudgets.map((c) => Number(c.value)),
        activityCode,
        start: queryStartDate
          ? queryStartDate
          : startDate
          ? String(startDate).split('/').reverse().join('-')
          : undefined,
        end: queryEndDate
          ? queryEndDate
          : endDate
          ? String(endDate).split('/').reverse().join('-')
          : undefined,
      })
        .then((response) => {
          const groupedByBudget = Object.values(
            response.reduce(
              (
                c: Record<number, RevenueCostReportResponse>,
                e: RevenueCostReportResponse,
              ) => {
                if (!c[e.budget_id]) {
                  c[e.budget_id] = e
                }
                return c
              },
              {},
            ),
          ).filter((item) => item.budget_total_without_margin !== null)

          const totalCost = groupedByBudget.reduce(
            (accumulator, currentValue) => {
              accumulator += parseFloat(
                currentValue.budget_total_without_margin,
              )
              return accumulator
            },
            0,
          )

          const totalRevenue = response.reduce((accumulator, currentValue) => {
            accumulator += parseFloat(currentValue.payment_value)
            return accumulator
          }, 0)

          const totalRevenueByActivity = response.reduce(
            (accumulator: Record<string, number>, currentValue) => {
              if (
                accumulator[
                  (currentValue.activity_name || '').replaceAll('"', '').trim()
                ]
              ) {
                accumulator[
                  (currentValue.activity_name || '').replaceAll('"', '').trim()
                ] += parseFloat(currentValue.payment_value)

                return accumulator
              }

              accumulator[
                (currentValue.activity_name || '').replaceAll('"', '').trim()
              ] = parseFloat(currentValue.payment_value)

              return accumulator
            },
            {},
          )

          const totalRevenueByBudget = groupedByBudget.reduce(
            (accumulator: Record<string, number>, currentValue) => {
              if (
                accumulator[
                  (currentValue.budget_name || '').replaceAll('"', '').trim()
                ]
              ) {
                accumulator[
                  (currentValue.budget_name || '').replaceAll('"', '').trim()
                ] += parseFloat(currentValue.budget_total_without_margin)

                return accumulator
              }

              accumulator[
                (currentValue.budget_name || '').replaceAll('"', '').trim()
              ] = parseFloat(currentValue.budget_total_without_margin)

              return accumulator
            },
            {},
          )

          const totalCostByBudgetWithPercentage = groupedByBudget.reduce(
            (accumulator: Record<number, string>, current) => {
              accumulator[current.budget_id] = (
                (parseFloat(current.budget_total_without_margin) / totalCost) *
                100
              ).toFixed(2)
              return accumulator
            },
            {},
          )

          const sortedByBudget = Object.entries(totalRevenueByBudget).sort(
            (a, b) => b[1] - a[1],
          )

          const sortedByActivity = Object.entries(totalRevenueByActivity).sort(
            (a, b) => b[1] - a[1],
          )

          const budgetsLabels = sortedByBudget
            .map((s) => s[0])
            .map((s) => {
              const budget = groupedByBudget.find(
                (item) => item.budget_name === s,
              )

              if (!budget) {
                return ''
              }

              return `${budget?.budget_name} - ${
                totalCostByBudgetWithPercentage[budget?.budget_id]
              }%: ${formatCurrencyUnit(
                parseFloat(budget?.budget_total_without_margin),
              )}`
            })
          const budgetsValues = sortedByBudget.map((s) => s[1])

          const activitiesNames = sortedByActivity.map((s) => s[0])
          const activitiesValues = sortedByActivity.map((s) => s[1])

          const totalRevenueByActivityWithPercentage = Object.entries(
            totalRevenueByActivity,
          ).reduce((accumulator: Record<string, string>, [key, value]) => {
            accumulator[key] = ((value / totalRevenue) * 100).toFixed(2)
            return accumulator
          }, {})

          const totalReceit = totalRevenue - totalCost

          const costPercentage = ((totalCost / totalRevenue) * 100).toFixed(2)
          const revenuePercentage = (100 - Number(costPercentage)).toFixed(2)

          const totalReceitMap = Object.entries({
            cost: totalCost,
            receit: totalReceit,
          }).sort((a, b) => b[1] - a[1])

          const totalReceitLabelsMap = {
            cost: `Custo total do orçamento - ${costPercentage}%: ${formatCurrencyUnit(
              totalCost,
            )}`,
            receit: `Margem de lucro (bruta) - ${revenuePercentage}%: ${formatCurrencyUnit(
              totalReceit,
            )}`,
          }

          const totalReceitLabels = totalReceitMap.map(
            (s) =>
              totalReceitLabelsMap[s[0] as keyof typeof totalReceitLabelsMap],
          )

          const totalReceitValues = totalReceitMap.map((s) => s[1])

          setTotalCostChartData({
            labels: budgetsLabels,
            datasets: [
              {
                data: budgetsValues,
                backgroundColor: CHART_COLORS,
                borderWidth: 1,
              },
            ],
          })

          setRevenueByActivityChartData({
            labels: activitiesNames.map(
              (activity) =>
                `${activity} - ${
                  totalRevenueByActivityWithPercentage[activity]
                }%: ${formatCurrencyUnit(totalRevenueByActivity[activity])}`,
            ),
            datasets: [
              {
                data: activitiesValues,
                backgroundColor: CHART_COLORS,
                borderWidth: 1,
              },
            ],
          })

          setReceitChartData({
            labels: totalReceitLabels,
            datasets: [
              {
                data: totalReceitValues,
                backgroundColor: CHART_COLORS,
                borderWidth: 1,
              },
            ],
          })

          setTotal(totalCost)
          setTotalRevenue(totalRevenue)
          setTotalReceit(totalReceit)

          if (!response.length) {
            openErrorToast({ message: 'Nenhum resultado encontrado.' })
          }
          setIsLoading(false)
        })
        .catch((error) => {
          openErrorToast({ message: 'Ocorreu um erro ao gerar gráfico!' })
          setIsLoading(false)
        })
    },
    [
      selectedInstitutions,
      selectedActivityStatus,
      selectedBudgets,
      activityCode,
      startDate,
      endDate,
      openErrorToast,
    ],
  )

  if (!institutions?.length || !budgets?.length || isLoading) {
    return <LoadingFullPage />
  }

  return (
    <Box component="main" sx={{ flexGrow: 1, py: 8 }}>
      <Container maxWidth={false}>
        <Box>
          <Box
            sx={{
              alignItems: 'center',
              display: 'flex',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
            }}
          >
            <Typography sx={{ m: 1 }} variant="h4">
              Gráfico de Receita/Custo
            </Typography>
          </Box>

          <Box
            sx={{
              alignItems: 'center',
              display: 'flex',
              justifyContent: 'flex-start',
              flexWrap: 'wrap',
              gap: 1,
            }}
          >
            <SelectWithCheckbox
              options={budgets}
              value={selectedBudgets}
              onSelect={setSelectedBudgets}
              selectAllLabel="Todas os orçamentos"
              placeholder="Orçamentos"
            />

            <SelectWithCheckbox
              options={institutions}
              value={selectedInstitutions}
              onSelect={setSelectedInstitutions}
              selectAllLabel="Todas as instituições"
              placeholder="Instituições"
            />

            <TextField
              label="Código da atividade"
              type="text"
              variant="outlined"
              value={activityCode}
              onChange={(event) => {
                setActivityCode(event.target.value)
              }}
            />

            <SelectWithCheckbox
              options={ACTIVITY_STATUS_OPTIONS.filter(
                (i) => i.value !== 'cancelled',
              )}
              value={selectedActivityStatus}
              onSelect={setSelectedActivityStatus}
              selectAllLabel="Todas as situações"
              placeholder="Status da atividade"
            />

            <DatePicker
              label="Data inicial"
              id="startDate"
              value={startDate}
              onChange={(newValue) => {
                setStartDate(newValue)
              }}
            />

            <DatePicker
              label="Data final"
              id="endDate"
              value={endDate}
              onChange={(newValue) => {
                setEndDate(newValue)
              }}
            />

            <Button
              color="primary"
              variant="contained"
              onClick={() => generateReport({})}
              disabled={
                !selectedInstitutions.length &&
                !selectedBudgets.length &&
                !startDate &&
                !endDate &&
                !activityCode
              }
            >
              Gerar relatório
            </Button>
          </Box>
        </Box>

        {totalCostChartData && (
          <Card sx={{ mt: 3 }}>
            <CardContent>
              <Pie
                data={totalCostChartData}
                width={600}
                height={600}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: {
                    tooltip: {
                      callbacks: {
                        label: (context) => formatCurrencyUnit(context.parsed),
                      },
                    },
                    title: {
                      display: true,
                      font: {
                        size: 16,
                      },
                      text: `Custo total sem margem: ${formatCurrencyUnit(
                        total,
                      )}`,
                    },
                  },
                }}
              />
            </CardContent>
          </Card>
        )}

        {revenueByActivityChartData && (
          <Card sx={{ mt: 3 }}>
            <CardContent>
              <Pie
                data={revenueByActivityChartData}
                width={600}
                height={600}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: {
                    tooltip: {
                      callbacks: {
                        label: (context) => formatCurrencyUnit(context.parsed),
                      },
                    },
                    title: {
                      display: true,
                      font: {
                        size: 16,
                      },
                      text: `Receita total por atividade: ${formatCurrencyUnit(
                        totalRevenue,
                      )}`,
                    },
                  },
                }}
              />
            </CardContent>
          </Card>
        )}

        {receitChartData && (
          <Card sx={{ mt: 3 }}>
            <CardContent>
              <Pie
                data={receitChartData}
                width={600}
                height={600}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: {
                    tooltip: {
                      callbacks: {
                        label: (context) => formatCurrencyUnit(context.parsed),
                      },
                    },
                    title: {
                      display: true,
                      font: {
                        size: 16,
                      },
                      text: `Margem de lucro (bruta): ${formatCurrencyUnit(
                        totalReceit,
                      )}`,
                    },
                  },
                }}
              />
            </CardContent>
          </Card>
        )}
      </Container>
    </Box>
  )
}
